import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Checkbox, FormControlLabel, IconButton, Stack, TextField, Typography, ClickAwayListener, Button, CircularProgress } from "@mui/material";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { useMemo } from "react";
import customFetch from "../../utils/customFetch";
import { ResponseConfigOption } from "../../types/common";
import { FieldRenderConfig } from "../../types/nodes";
import { WorkflowNode } from "../../types/workflow";
import InputTextBoxWithDropdown from "../Common/InputTextBoxWithDropdown";
import LabelWithHint from "../Common/LabelWithHint";
import SelectDropdown from "../Common/SelectDropdown";
import ExecutionConditions from "../ExecutionConditions/ExecutionConditions";
import LineItemsConfigField from "./LineItemsConfigField";

interface MapEntry {
    key: string;
    value: string;
    type: 'string' | 'number' | 'boolean';
    keyRefNodeId?: string;
    valueRefNodeId?: string;
}

interface ListEntry {
    id: string;
    values: Record<string, string>;
}

const LoadingButton: React.FC<{
    keyName: string;
    renderConfig: FieldRenderConfig;
    displayValue: string;
    workflowNode: WorkflowNode;
}> = ({ keyName, renderConfig, displayValue, workflowNode }) => {
    const [loading, setLoading] = React.useState(false);
    const disabled = (renderConfig as any)?.read_only || loading;
    return (
        <Button variant="contained" disabled={disabled} onClick={async () => {
            try {
                setLoading(true);
                const payloadKeys = (renderConfig as any)?.action_payload_keys || [];
                const body: Record<string, any> = {};
                payloadKeys.forEach((k: string) => {
                    if (k === 'node_id') body[k] = workflowNode?.node?.id;
                    else body[k] = (displayValue && k === keyName ? displayValue : '') || '';
                });
                await customFetch((renderConfig as any)?.action_url, {
                    method: (renderConfig as any)?.action_method || 'POST',
                    body: JSON.stringify(body),
                });
            } finally {
                setLoading(false);
            }
        }}>
            {loading ? <CircularProgress size={18} /> : (renderConfig?.label || keyName)}
        </Button>
    );
};

const MultipleSelectField: React.FC<{
    keyName: string;
    renderConfig: FieldRenderConfig;
    displayValue: string;
    setNodeRunConfigAndRefNodeId: (key: string, value: string) => void;
    saveConfigField: (key: string) => void;
    filterText: string;
}> = ({ keyName, renderConfig, displayValue, setNodeRunConfigAndRefNodeId, saveConfigField, filterText }) => {
    const [localSelectedValues, setLocalSelectedValues] = React.useState<string[]>(
        displayValue ? displayValue.split(',').map(v => v.trim()).filter(Boolean) : []
    );

    React.useEffect(() => {
        setLocalSelectedValues(displayValue ? displayValue.split(',').map(v => v.trim()).filter(Boolean) : []);
    }, [displayValue]);

    return (
        <Box>
            <LabelWithHint label={renderConfig?.label || keyName}
                hint={renderConfig?.placeholder}
                required={renderConfig?.required || false}
            />
            <Stack spacing={1} sx={{ mt: 1, border: '1px solid #e0e0e0', borderRadius: '4px', p: 1 }}>
                {(renderConfig?.options || []).filter(opt => opt.label.toLowerCase().includes(filterText.toLowerCase())).map((opt) => (
                    <FormControlLabel
                        key={opt.value}
                        control={
                            <Checkbox size="small"
                                checked={localSelectedValues.includes(opt.value)}
                                onChange={(e) => {
                                    const newValues = e.target.checked
                                        ? [...localSelectedValues, opt.value]
                                        : localSelectedValues.filter(v => v !== opt.value);
                                    setLocalSelectedValues(newValues);
                                    const newValue = newValues.join(',');
                                    setNodeRunConfigAndRefNodeId(keyName, newValue);
                                    saveConfigField(keyName);
                                }}
                            />
                        }
                        label={opt.label}
                        sx={{
                            marginLeft: 0,
                            marginRight: 0,
                            width: '100%'
                        }}
                    />
                ))}
            </Stack>
        </Box>
    );
};

const MapField: React.FC<{
    keyName: string;
    renderConfig: FieldRenderConfig;
    displayValue: string;
    setNodeRunConfigAndRefNodeId: (key: string, value: string, refNodeId?: string) => void;
    saveConfigField: (key: string) => void;
    dropdownOptionIdToOptionMap: Record<string, ResponseConfigOption>;
    countToNameMap: Record<string, string>;
    refNodeId: string;
    responseConfig: ResponseConfigOption[];
}> = ({ keyName, renderConfig, displayValue, setNodeRunConfigAndRefNodeId, saveConfigField, dropdownOptionIdToOptionMap, countToNameMap, refNodeId, responseConfig }) => {
    const parseMapValue = (value: string): MapEntry[] => {
        try {
            const parsed = JSON.parse(value || '{}');
            // If map_options exist, add any missing default entries
            if (renderConfig?.map_options?.length) {
                renderConfig.map_options.forEach((option: { key: string; defaultValue?: any; value?: any }) => {
                    if (!(option.key in parsed)) {
                        parsed[option.key] = option.value ?? option.defaultValue;
                    }
                });
            }

            return Object.entries(parsed).map(([key, value]) => ({
                key,
                value: String(value),
                type: (typeof value === 'number' || typeof value === 'boolean')
                    ? typeof value as 'number' | 'boolean'
                    : 'string',
                keyRefNodeId: undefined,
                valueRefNodeId: undefined
            }));
        } catch {
            // If parsing fails and map_options exist, initialize with default values
            if (renderConfig?.map_options?.length) {
                return renderConfig.map_options.map((option: { key: string; label: string; defaultValue?: any; value?: any }) => {
                    const val = option.value ?? option.defaultValue;
                    return {
                        key: option.key,
                        value: String(val),
                        type: (typeof val === 'number' || typeof val === 'boolean')
                            ? typeof val as 'number' | 'boolean'
                            : 'string',
                        keyRefNodeId: undefined,
                        valueRefNodeId: undefined
                    };
                });
            }
            return [];
        }
    };

    const [mapEntries, setMapEntries] = React.useState<MapEntry[]>(() => parseMapValue(displayValue));

    React.useEffect(() => {
        setMapEntries(parseMapValue(displayValue));
    }, [displayValue]);

    const updateMapEntries = (newEntries: MapEntry[]) => {
        setMapEntries(newEntries);
        const newValue = newEntries.reduce((acc, { key, value, type }) => {
            if (!key || key.trim() === '') return acc;
            acc[key] = type === 'number' ? Number(value) :
                type === 'boolean' ? value === 'true' :
                value;
            return acc;
        }, {} as Record<string, any>);
        
        // Only update if there are valid entries
        if (Object.keys(newValue).length > 0) {
            setNodeRunConfigAndRefNodeId(keyName, JSON.stringify(newValue));
        }
    };

    return (
        <Box>
            <LabelWithHint
                label={renderConfig?.label || keyName}
                hint={renderConfig?.placeholder}
                required={renderConfig?.required || false}
            />
            <Stack spacing={2}>
                {mapEntries.map((entry, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                        {renderConfig?.map_options ? (
                            <SelectDropdown size="small" placeholder="Key"
                                additionalClasses="w-[35%]"
                                selectionOptions={renderConfig.map_options.map((opt: { key: string; label: string }) => ({
                                    id: opt.key,
                                    title: opt.label
                                }))}
                                defaultSelected={{
                                    id: entry.key,
                                    title: renderConfig.map_options.find((opt: { key: string; label: string }) => opt.key === entry.key)?.label || entry.key
                                }}
                                onOptionSelection={(value) => {
                                    const newEntries = [...mapEntries];
                                    const mapOption = renderConfig.map_options?.find((opt) => opt.key === value);
                                    const val = (mapOption && ('value' in mapOption ? mapOption.value : mapOption.defaultValue)) ?? '';
                                    newEntries[index] = {
                                        key: value,
                                        value: String(val),
                                        type: (typeof val === 'number' || typeof val === 'boolean')
                                            ? typeof val as 'number' | 'boolean'
                                            : 'string'
                                    };
                                    updateMapEntries(newEntries);
                                }}
                            />
                        ) : (
                            <InputTextBoxWithDropdown
                                key={`key-${index}-${entry.keyRefNodeId || 'static'}`}
                                editableInput={entry.key}
                                setEditableValue={(textValue, refNodeId) => {
                                    const newEntries = [...mapEntries];
                                    newEntries[index].key = textValue;
                                    newEntries[index].keyRefNodeId = refNodeId;
                                    setMapEntries(newEntries);
                                }}
                                refNodeId={entry.keyRefNodeId || refNodeId}
                                dropdownOptions={responseConfig}
                                cssOverrides="w-[35%]"
                                saveOnBlur={() => {
                                    updateMapEntries(mapEntries);
                                    saveConfigField(keyName);
                                }}
                                dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                                countToNameMap={countToNameMap}
                            />
                        )}
                        <SelectDropdown
                            size="small"
                            additionalClasses="w-[100px]"
                            placeholder="Type"
                            selectionOptions={
                                renderConfig?.options?.filter(
                                    opt => ['string', 'number', 'boolean'].includes(opt.value)                                ).map(opt => ({
                                    id: opt.value,
                                    title: opt.label
                                })) || [
                                    { id: 'string', title: 'String (Default)' },
                                    { id: 'number', title: 'Number' },
                                    { id: 'boolean', title: 'Boolean' }
                                ]
                            }
                            defaultSelected={{
                                id: entry.type,
                                title: entry.type.charAt(0).toUpperCase() + entry.type.slice(1)
                            }}
                            onOptionSelection={(value) => {
                                const newEntries = [...mapEntries];
                                newEntries[index].type = value as 'string' | 'number' | 'boolean';
                                updateMapEntries(newEntries);
                            }}
                        />
                        <InputTextBoxWithDropdown
                            key={`value-${index}-${entry.valueRefNodeId || 'static'}`}
                            editableInput={entry.value}
                            setEditableValue={(textValue, refNodeId) => {
                                const newEntries = [...mapEntries];
                                newEntries[index].value = textValue;
                                newEntries[index].valueRefNodeId = refNodeId;
                                setMapEntries(newEntries);
                            }}
                            refNodeId={entry.valueRefNodeId || refNodeId}
                            dropdownOptions={responseConfig}
                            cssOverrides="w-[35%]"
                            saveOnBlur={() => {
                                updateMapEntries(mapEntries);
                                saveConfigField(keyName);
                            }}
                            dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                            countToNameMap={countToNameMap}
                        />
                        <IconButton size="small"
                            onClick={() => {
                                const newEntries = mapEntries.filter((_, i) => i !== index);
                                updateMapEntries(newEntries);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Stack>
                ))}
                <IconButton size="small" sx={{ alignSelf: 'flex-start' }}
                    onClick={() => {
                        // Don't add new entry if there's already an empty one
                        const hasEmptyEntry = mapEntries.some(entry => !entry.key || entry.key.trim() === '');
                        if (hasEmptyEntry) return;
                        
                        const opts = renderConfig?.map_options;
                        const first = opts?.[0];
                        const key = first?.key ?? '';
                        const optVal = first && ('value' in first ? first.value : first.defaultValue);
                        const val = first ? String(optVal ?? '') : '';
                        let type: 'string' | 'number' | 'boolean' = 'string';
                        if (first && (typeof optVal === 'number' || typeof optVal === 'boolean')) {
                            type = typeof optVal as 'number' | 'boolean';
                        }
                        updateMapEntries([...mapEntries, { 
                            key, 
                            value: val, 
                            type,
                            keyRefNodeId: undefined,
                            valueRefNodeId: undefined
                        }]);
                    }}
                >
                    <AddIcon />
                </IconButton>
            </Stack>
        </Box>
    );
};

const ListField: React.FC<{
    keyName: string;
    renderConfig: FieldRenderConfig;
    displayValue: string;
    setNodeRunConfigAndRefNodeId: (key: string, value: string) => void;
    saveConfigField: (key: string) => void;
}> = ({ keyName, renderConfig, displayValue, setNodeRunConfigAndRefNodeId, saveConfigField }) => {
    const [focusedField, setFocusedField] = React.useState<{index: number, key: string} | null>(null);
    const fieldRefs = React.useRef<{[key: string]: HTMLInputElement}>({});

    const parseListValue = (value: string): ListEntry[] => {
        try {
            const parsed = JSON.parse(value || '[]');
            return parsed.map((item: any) => ({
                id: item.id || crypto.randomUUID(),
                values: item
            }));
        } catch {
            return [];
        }
    };

    const [listEntries, setListEntries] = React.useState<ListEntry[]>(() => parseListValue(displayValue));

    React.useEffect(() => {
        setListEntries(parseListValue(displayValue));
    }, [displayValue]);

    const updateListEntries = (newEntries: ListEntry[]) => {
        setListEntries(newEntries);
        const newValue = newEntries.map(entry => entry.values);
        setNodeRunConfigAndRefNodeId(keyName, JSON.stringify(newValue));
    };

    // Add effect to restore focus after re-render
    React.useEffect(() => {
        if (focusedField) {
            const fieldId = `${focusedField.index}-${focusedField.key}`;
            const element = fieldRefs.current[fieldId];
            if (element) {
                element.focus();
            }
        }
    }, [listEntries, focusedField]);

    return (
        <ClickAwayListener onClickAway={() => setFocusedField(null)}>
            <Box>
                <LabelWithHint
                    label={renderConfig?.label || keyName}
                    hint={renderConfig?.placeholder}
                    required={renderConfig?.required || false}
                />
                <Stack spacing={2}>
                    {listEntries.map((entry, index) => (
                        <Stack key={entry.id} spacing={2} sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: '4px' }}>
                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontSize: '0.75rem' }}>{renderConfig?.row_label || 'Item'} {index + 1}</Typography>
                                <IconButton size="small"
                                    onClick={() => {
                                        const newEntries = listEntries.filter((_, i) => i !== index);
                                        updateListEntries(newEntries);
                                        saveConfigField(keyName);
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                            <Stack spacing={2}>
                                {renderConfig.schema?.map((field) => (
                                    <TextField
                                        key={field.key}
                                        inputRef={(el) => {
                                            if (el) {
                                                fieldRefs.current[`${index}-${field.key}`] = el;
                                            }
                                        }}
                                        size="small"
                                        label={field.label}
                                        placeholder={field.placeholder}
                                        value={entry.values[field.key] || ''}
                                        onChange={(e) => {
                                            const newEntries = [...listEntries];
                                            newEntries[index].values[field.key] = e.target.value;
                                            updateListEntries(newEntries);
                                        }}
                                        onFocus={() => setFocusedField({ index, key: field.key })}
                                        onBlur={() => {
                                            setFocusedField(null);
                                            saveConfigField(keyName);
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    ))}
                    <IconButton 
                        size="small" 
                        sx={{ alignSelf: 'flex-start' }}
                        onClick={() => {
                            const newEntry: ListEntry = {
                                id: crypto.randomUUID(),
                                values: {}
                            };
                            updateListEntries([...listEntries, newEntry]);
                            saveConfigField(keyName);
                        }}
                    >
                        <AddIcon />
                    </IconButton>
                </Stack>
            </Box>
        </ClickAwayListener>
    );
};

type RunConfigFieldProps = {
    openConditionsDetails: boolean,
    localConditions: any,
    removeCondition: any,
    setLocalConditions: any,
    responseConfig: any,
    isAnyStepIncomplete: boolean,
    setConditionsOpenDetails: (openDetails: boolean) => void,
    addNewCondition: () => void,
    onSave: (action?: string, cb?: () => void, isIntermittentSave?: boolean) => void,
    workflowNode: WorkflowNode,
    keyName: string;
    renderConfig: FieldRenderConfig;
    setNodeRunConfigAndRefNodeId: (key: string, value: string, refNodeId?: string) => void;
    refNodeId: string;
    displayValue: string;
    saveConfigField: (key: string) => void;
    filterText: string;
    countToNameMap: Record<string, string>;
    isFetchingSavedNodeConfig: boolean;
};

const RunConfigField: React.FC<RunConfigFieldProps> = ({
    keyName,
    setNodeRunConfigAndRefNodeId,
    responseConfig,
    displayValue,
    renderConfig,
    refNodeId,
    saveConfigField,
    openConditionsDetails,
    localConditions,
    removeCondition,
    setLocalConditions,
    isAnyStepIncomplete,
    setConditionsOpenDetails,
    addNewCondition,
    onSave,
    workflowNode,
    filterText,
    countToNameMap,
    isFetchingSavedNodeConfig,
}) => {
    const dropdownOptionIdToOptionMap = useMemo(() => {
        let dropdownOptionIdToOptionMap: Record<string, ResponseConfigOption> = {};
        if (responseConfig?.length) {
            responseConfig.forEach((option: ResponseConfigOption) => {
                dropdownOptionIdToOptionMap[`${option.id}:${option.refNodeId}`] = {
                    ...option,
                    value: typeof option.value !== "object" ? option.value : ""
                }
            });
        }

        return dropdownOptionIdToOptionMap;
    }, [responseConfig]);

    const renderField = () => {
        switch (renderConfig?.type) {
            case "button":
                return (
                    <Box>
                        <LoadingButton
                            keyName={keyName}
                            renderConfig={renderConfig}
                            displayValue={displayValue}
                            workflowNode={workflowNode}
                        />
                    </Box>
                );
            case "filterConditions":
                return (
                    <ExecutionConditions
                        dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                        openConditionsDetails={openConditionsDetails || !!(Array.isArray(localConditions) && localConditions.length)}
                        localConditions={localConditions}
                        removeCondition={removeCondition}
                        setLocalConditions={setLocalConditions}
                        responseConfig={responseConfig}
                        isAnyStepIncomplete={isAnyStepIncomplete}
                        setConditionsOpenDetails={setConditionsOpenDetails}
                        addNewCondition={addNewCondition}
                        onSave={onSave}
                        workflowNode={workflowNode}
                    />
                )
            case "singleSelect":
            case "select":
                return !filterText || renderConfig?.label?.toLowerCase().includes(filterText.toLowerCase()) ? (
                    <>
                        <LabelWithHint
                            label={renderConfig?.label || keyName}
                            hint={renderConfig?.placeholder}
                            required={renderConfig?.required || false}
                        />
                        <SelectDropdown additionalClasses="mt-1"
                            selectionOptions={
                                (renderConfig?.options || []).map(opt => ({
                                    id: opt.value,
                                    title: opt.label,
                                }))
                            }
                            placeholder={renderConfig?.placeholder}
                            onOptionSelection={(value) => {
                                setNodeRunConfigAndRefNodeId(keyName, value);
                                saveConfigField(keyName);
                            }}
                            defaultSelected={
                                displayValue
                                    ? {
                                        id: displayValue,
                                        title:
                                            renderConfig?.options?.find(
                                                opt => opt.value === displayValue
                                            )?.label || '',
                                        subtitle: ''
                                    }
                                    : undefined
                            }
                            size="small"
                            optionsLoading={isFetchingSavedNodeConfig}
                        />
                    </>
                ) : null;
            case "multipleSelect":
                return (
                    <MultipleSelectField
                        keyName={keyName}
                        renderConfig={renderConfig}
                        displayValue={displayValue}
                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                        saveConfigField={saveConfigField}
                        filterText={filterText}
                    />
                );

            // currently vars are not supported here
            // case "textarea":
            //     return !filterText || renderConfig?.label?.toLowerCase().includes(filterText.toLowerCase()) ? (
            //         <>
            //             <LabelWithHint
            //                 label={renderConfig?.label || keyName}
            //                 hint={renderConfig?.placeholder}
            //                 required={renderConfig?.required || false}
            //             />
            //             <TextField fullWidth multiline
            //                 defaultValue={displayValue}
            //                 onChange={(e) => {
            //                     setNodeRunConfigAndRefNodeId(keyName, e.target.value);
            //                 }}
            //                 onBlur={() => saveConfigField(keyName)}
            //                 placeholder={renderConfig?.placeholder}
            //                 rows={6}
            //                 maxRows={20}
            //                 size="small"
            //                 sx={{
            //                     '& .MuiInputBase-root': {
            //                         fontFamily: 'monospace',  // Better for code
            //                     }
            //                 }}
            //             />
            //         </>
            //     ) : null;

            case "bool":
            case "boolean":
                const Component = Checkbox;
                return !filterText || renderConfig?.label?.toLowerCase().includes(filterText.toLowerCase()) ? (
                    <FormControlLabel
                        control={
                            <Component
                                checked={displayValue === 'true'}
                                onChange={(e) => {
                                    setNodeRunConfigAndRefNodeId(keyName, e.target.checked.toString());
                                    saveConfigField(keyName);
                                }}
                                size="small"
                            />
                        }
                        label={
                            <LabelWithHint
                                label={renderConfig?.label || keyName}
                                hint={renderConfig?.placeholder}
                                required={renderConfig?.required || false}
                            />
                        }
                    />
                ) : null;

            case "date":
            case "datetime":
                return !filterText || renderConfig?.label?.toLowerCase().includes(filterText.toLowerCase()) ? (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            value={displayValue ? new Date(displayValue) : null}
                            onChange={(newValue: Date | null) => {
                                setNodeRunConfigAndRefNodeId(keyName, newValue ? newValue.toISOString() : '');
                                saveConfigField(keyName);
                            }}
                            format={renderConfig?.type === 'date' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm'}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    placeholder: renderConfig?.placeholder,
                                    fullWidth: true
                                }
                            }}
                        />
                    </LocalizationProvider>
                ) : null;
            case "map":
                return null;
                // this is causing issues with airtable create record
                // return (
                //     <MapField
                //         keyName={keyName}
                //         renderConfig={renderConfig}
                //         displayValue={displayValue}
                //         setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                //         saveConfigField={saveConfigField}
                //     />
                // );
            case "map_with_type":
                return (
                    <MapField
                        keyName={keyName}
                        renderConfig={renderConfig}
                        displayValue={displayValue}
                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                        saveConfigField={saveConfigField}
                        dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                        countToNameMap={countToNameMap}
                        refNodeId={refNodeId}
                        responseConfig={responseConfig}
                    />
                );
            case "hidden":
                return null;
            case "list":
                return (
                    <ListField
                        keyName={keyName}
                        renderConfig={renderConfig}
                        displayValue={displayValue}
                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                        saveConfigField={saveConfigField}
                    />
                );
            case "lineItemMapping":
                return (
                    <LineItemsConfigField
                        keyName={keyName}
                        renderConfig={renderConfig}
                        displayValue={displayValue}
                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                        saveConfigField={saveConfigField}
                        dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                        countToNameMap={countToNameMap}
                        refNodeId={refNodeId}
                        responseConfig={responseConfig}
                        filterText={filterText}
                    />
                );
            default:
                return !filterText || (renderConfig?.label || keyName)?.toLowerCase().includes(filterText.toLowerCase()) ? (
                    <>
                        <LabelWithHint
                            label={renderConfig?.label || keyName}
                            hint={renderConfig?.placeholder}
                            required={renderConfig?.required || false}
                        />
                        <InputTextBoxWithDropdown
                            editableInput={displayValue}
                            setEditableValue={(textValue, refNodeId) => {
                                setNodeRunConfigAndRefNodeId(keyName, textValue?.trim(), refNodeId);
                            }}
                            refNodeId={refNodeId}
                            dropdownOptions={responseConfig}
                            cssOverrides="mb-4"
                            saveOnBlur={() => saveConfigField(keyName)}
                            dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                            countToNameMap={countToNameMap}
                        />
                    </>
                ) : null;
        }
    };

    return renderField();
};

export default RunConfigField;
